import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShop } from '../contexts/ShopContext';
import { shopService } from '../services/shopService';

const ProfilePage: React.FC = () => {
  const { currentUser, changePassword, refreshUser, logout } = useAuth();
  const { userRole } = useShop();
  const navigate = useNavigate();

  // Display name state
  const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameSuccess, setNameSuccess] = useState('');
  const [nameError, setNameError] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleUpdateDisplayName = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed || trimmed.length < 2) {
      setNameError('Display name must be at least 2 characters.');
      return;
    }
    if (trimmed.length > 100) {
      setNameError('Display name must be 100 characters or less.');
      return;
    }

    setNameLoading(true);
    setNameError('');
    setNameSuccess('');

    try {
      await shopService.updateDisplayName(trimmed);
      // Reload the Firebase user to pick up the new display name
      await new Promise(resolve => setTimeout(resolve, 500));
      refreshUser();
      setNameSuccess('Display name updated successfully.');
    } catch (err: any) {
      setNameError(err.message || 'Failed to update display name.');
    } finally {
      setNameLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      await changePassword(currentPassword, newPassword);
      // Revoke all other sessions
      try {
        await shopService.logoutAllDevices();
        console.log('Successfully revoked all other sessions');
      } catch (logoutErr) {
        console.error('Failed to revoke other sessions:', logoutErr);
      }
      // Sign out so user re-authenticates with new password
      await logout();
      navigate('/login');
    } catch (err: any) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPasswordError('Current password is incorrect.');
      } else if (err.code === 'auth/weak-password') {
        setPasswordError('New password is too weak. Please use at least 6 characters.');
      } else {
        setPasswordError(err.message || 'Failed to change password.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const sectionStyle: React.CSSProperties = {
    backgroundColor: 'var(--cool-white)',
    border: '1px solid var(--cool-gray)',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '24px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--charcoal)',
    marginBottom: '6px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.875rem',
    border: '1px solid var(--cool-gray)',
    borderRadius: '6px',
    backgroundColor: 'var(--delft-frost)',
    color: 'var(--charcoal)',
    boxSizing: 'border-box',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '0.875rem',
    fontWeight: 600,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    backgroundColor: 'var(--delft-blue)',
    color: 'white',
  };

  const disabledButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'var(--cool-gray)',
    color: 'var(--steel-gray)',
    cursor: 'not-allowed',
  };

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '32px 16px' }}>
      <button
        onClick={() => navigate('/')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          background: 'none',
          border: 'none',
          color: 'var(--delft-blue)',
          cursor: 'pointer',
          fontSize: '0.875rem',
          padding: 0,
          marginBottom: '16px',
        }}
      >
        ← Back to Dashboard
      </button>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--charcoal)', marginBottom: '8px' }}>
        Profile
      </h1>
      <p style={{ fontSize: '0.875rem', color: 'var(--steel-gray)', marginBottom: '24px' }}>
        {currentUser?.email}
        {userRole && (
          <span style={{
            marginLeft: '8px',
            padding: '2px 8px',
            backgroundColor: 'var(--bg-accent)',
            color: 'var(--primary-dark)',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: 500,
          }}>
            {userRole}
          </span>
        )}
      </p>

      {/* Display Name Section */}
      <div style={sectionStyle}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--charcoal)', marginBottom: '16px' }}>
          Display Name
        </h2>
        <form onSubmit={handleUpdateDisplayName}>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              style={inputStyle}
              placeholder="Enter your display name"
              maxLength={100}
            />
          </div>

          {nameError && (
            <p style={{ fontSize: '0.813rem', color: 'var(--error-text)', marginBottom: '12px' }}>{nameError}</p>
          )}
          {nameSuccess && (
            <p style={{ fontSize: '0.813rem', color: 'var(--success-dark)', marginBottom: '12px' }}>{nameSuccess}</p>
          )}

          <button
            type="submit"
            disabled={nameLoading || !displayName.trim() || displayName.trim() === (currentUser?.displayName || '')}
            style={nameLoading || !displayName.trim() || displayName.trim() === (currentUser?.displayName || '') ? disabledButtonStyle : buttonStyle}
          >
            {nameLoading ? 'Saving...' : 'Save Display Name'}
          </button>
        </form>
      </div>

      {/* Change Password Section */}
      <div style={sectionStyle}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--charcoal)', marginBottom: '8px' }}>
          Change Password
        </h2>
        <p style={{ fontSize: '0.813rem', color: 'var(--steel-gray)', marginBottom: '16px' }}>
          You will be signed out and redirected to the login page after changing your password.
        </p>
        <form onSubmit={handleChangePassword}>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={inputStyle}
              autoComplete="current-password"
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={inputStyle}
              autoComplete="new-password"
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
              autoComplete="new-password"
            />
          </div>

          {passwordError && (
            <p style={{ fontSize: '0.813rem', color: 'var(--error-text)', marginBottom: '12px' }}>{passwordError}</p>
          )}
          {passwordSuccess && (
            <p style={{ fontSize: '0.813rem', color: 'var(--success-dark)', marginBottom: '12px' }}>{passwordSuccess}</p>
          )}

          <button
            type="submit"
            disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
            style={passwordLoading || !currentPassword || !newPassword || !confirmPassword ? disabledButtonStyle : buttonStyle}
          >
            {passwordLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
